import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Color } from '../../../styles/Color';
import CallToActionLink from './CallToActionLink';
import {
	Css,
	ICallToActionLink,
	ITheme,
	Markdown,
	Responsive,
	StyledCallToAction,
} from '../../../ui';
import { ICallToActionTheme } from './ICallToActionTheme';

interface ICallToActions {
	ctaTheme?: ICallToActionTheme;
	title: string;
	body?: ReactNode | string;
	links?: ICallToActionLink[];
	hasDescription?: boolean;
	children?: ReactNode;
}

export default function CallToAction({
	ctaTheme = 'grey',
	title,
	body,
	links,
	hasDescription = true,
	children,
}: ICallToActions) {
	return (
		<StyledCallToActionWrap ctaTheme={ctaTheme} hasDescription={hasDescription}>
			<BodyWrap ctaTheme={ctaTheme}>
				<CallToActionTitle ctaTheme={ctaTheme}>{title}</CallToActionTitle>
				{children || <Markdown source={body as string} />}
			</BodyWrap>
			{links && links.length > 0 && (
				<ButtonWrap>
					{links.map(
						(link, index) =>
							link && (
								<CallToActionLink
									key={link.key}
									link={link}
									ctaTheme={ctaTheme}
									secondary={link.secondary ?? index === 1}
									ariaLabel={link.title}
								/>
							),
					)}
				</ButtonWrap>
			)}
		</StyledCallToActionWrap>
	);
}

const StyledCallToActionWrap = styled(StyledCallToAction)<
	{
		ctaTheme: ICallToActionTheme;
	},
	ITheme
>`
	align-items: flex-end;
	background-color: ${({ ctaTheme, theme }) => {
		if (ctaTheme === 'primary') return theme.callToAction.background.inverted;
		if (ctaTheme === 'orange') return Color.Tertiary2;
		return theme.callToAction?.background?.default;
	}};

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const CallToActionTitle = styled.h4<
	{
		ctaTheme: ICallToActionTheme;
	},
	ITheme
>`
	color: ${({ ctaTheme, theme }) => {
		if (ctaTheme === 'grey' || ctaTheme === 'orange')
			return theme.callToAction.font.color.default;
		return theme.callToAction.font.color.inverted;
	}};

	line-height: normal;
	margin-bottom: 0.2em;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(32)};
	}
`;

const BodyWrap = styled.div<{ ctaTheme: ICallToActionTheme }, ITheme>`
	max-width: 55%;

	color: ${({ ctaTheme, theme }) => {
		if (ctaTheme === 'grey' || ctaTheme === 'orange')
			return theme.callToAction.font.color.default;
		return theme.callToAction.font.color.inverted;
	}};

	ul,
	p {
		margin: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		margin-bottom: ${Css.pxToRem(24)};
		max-width: 100%;
	}
`;

const ButtonWrap = styled.div<{}, ITheme>`
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: ${(props) => props.theme.grid.gutter.horizontal.default};

	a {
		text-decoration: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		align-items: flex-start;
		gap: ${(props) => props.theme.grid.gutter.vertical.mobile};
	}
`;
